import { MarkdownText } from './MarkdownText';
import { CitationChip } from '@/components/app/CitationChip';

/**
 * Renders AI output as styled markdown AND turns inline `[chunk:NNN]` citation
 * tokens into citation pills — so no AI surface ever shows a raw `[chunk:123]`.
 *
 * - With `wsId`: pills are clickable `CitationChip`s that open the source chunk.
 * - Without `wsId` (public share / demo): pills are static `#NNN` badges.
 *
 * Text between citations renders through `MarkdownText` (GFM tables, headings,
 * code, blockquotes). This is the single component every agent surface uses.
 */
// Chunk ids are opaque (numeric row ids historically; ULIDs from the Python
// engine now), so match any non-`]` id — never leave a raw [chunk:..] on screen.
const CITE_RE = /\[chunk:([^\]]+)\]/g;

export function CitedMarkdown({
  content,
  wsId,
  className,
}: {
  content: string;
  wsId?: string;
  className?: string;
}) {
  const parts: { kind: 'text' | 'chunk'; value: string; n: number }[] = [];
  // Map each distinct chunk id to a stable, human citation number [1], [2], … in
  // first-seen order — so a long ULID never shows on screen.
  const numberFor = new Map<string, number>();
  let cursor = 0;
  let m: RegExpExecArray | null;
  CITE_RE.lastIndex = 0;
  while ((m = CITE_RE.exec(content))) {
    if (m.index > cursor) parts.push({ kind: 'text', value: content.slice(cursor, m.index), n: 0 });
    const id = m[1];
    if (!numberFor.has(id)) numberFor.set(id, numberFor.size + 1);
    parts.push({ kind: 'chunk', value: id, n: numberFor.get(id)! });
    cursor = m.index + m[0].length;
  }
  if (cursor < content.length) parts.push({ kind: 'text', value: content.slice(cursor), n: 0 });

  // Fast path: no citations → plain styled markdown.
  if (!parts.some((p) => p.kind === 'chunk')) {
    return <MarkdownText className={className}>{content}</MarkdownText>;
  }

  return (
    <div className={className ?? 'leading-relaxed text-ink-900 space-y-1'}>
      {parts.map((p, i) =>
        p.kind === 'text' ? (
          <MarkdownText key={i}>{p.value}</MarkdownText>
        ) : wsId ? (
          <CitationChip key={i} wsId={wsId} chunkId={p.value} label={p.n} inline />
        ) : (
          <span
            key={i}
            className="inline-flex items-center align-baseline rounded bg-citation-50 text-citation-700 border border-citation-200 px-1.5 text-[11px] font-medium num"
          >
            {p.n}
          </span>
        ),
      )}
    </div>
  );
}
